import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { CONTACT, NAV_LINKS } from "@/lib/site";

export const Wordmark = ({ dark = false }) => (
  <span className="flex items-center gap-3">
    <img src="/images/logo-emblem.png" alt="LooXury Rentals emblem" className="h-9 w-9 object-contain" />
    <span className="leading-none">
      <span className={`block font-serif text-2xl tracking-tight ${dark ? "text-stone-50" : "text-stone-950"}`}>
        Loo<span className="bronze-text">X</span>ury
      </span>
      <span className={`mt-1 block text-[11px] font-medium tracking-[0.35em] uppercase ${dark ? "text-stone-400" : "text-stone-500"}`}>
        Rentals
      </span>
    </span>
  </span>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const home = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const href = (h) => (home ? h : `/${h}`);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-500 ${
        scrolled ? "bg-stone-50/85 backdrop-blur-xl border-stone-200/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" : "bg-stone-50/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="LooXury Rentals home">
          <Wordmark />
        </Link>
        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={href(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="link-underline text-sm font-normal tracking-wide text-stone-700 hover:text-stone-950 transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden items-center gap-6 lg:flex">
          <a href={CONTACT.phoneHref} data-testid="nav-phone-link" className="text-sm tracking-wide text-stone-700 hover:text-brand-bronze-dark transition-colors duration-300">
            {CONTACT.phoneDisplay}
          </a>
          <a
            href={href("#quote")}
            data-testid="nav-quote-button"
            className="bg-stone-900 px-6 py-2.5 text-sm tracking-wide text-stone-50 transition-colors duration-300 hover:bg-brand-bronze hover:text-stone-900"
          >
            Request a Quote
          </a>
        </div>
        <button
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
          className="text-stone-900 lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav data-testid="nav-mobile-menu" aria-label="Mobile" className="border-t border-stone-200/60 bg-stone-50/95 backdrop-blur-xl lg:hidden">
          <div className="flex flex-col gap-1 px-6 py-6">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={href(l.href)}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setOpen(false)}
                className="py-3 font-serif text-2xl font-light text-stone-900"
              >
                {l.label}
              </a>
            ))}
            <a
              href={href("#quote")}
              data-testid="nav-mobile-quote-button"
              onClick={() => setOpen(false)}
              className="mt-4 bg-stone-900 px-6 py-3 text-center text-sm tracking-wide text-stone-50"
            >
              Request a Quote
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
